// E-Mail Service mit IONOS SMTP Backend
// Automatische URL-Erkennung für Vercel und lokale Entwicklung
const getApiUrl = () => {
  // Wenn in Produktion (Vercel), nutze relative URL
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  // Lokale Entwicklung
  return process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
};

const emailConfig = {
  apiUrl: getApiUrl(),
  companyEmail: 'info@mgs-services.de',
  companyName: 'MGS Gebäudeservice'
};

/**
 * Basis E-Mail Versand Funktion über IONOS SMTP Backend
 * @param {Object} emailData - E-Mail Daten
 * @param {string} formType - Art des Formulars für Fehlerbehandlung
 * @returns {Promise} - Erfolg oder Fehler
 */
const sendEmail = async (emailData, formType = 'Formular') => {
  try {
    const response = await fetch(`${emailConfig.apiUrl}/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailData)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'E-Mail konnte nicht gesendet werden');
    }

    console.log(`${formType} erfolgreich gesendet:`, data);
    return {
      success: true,
      message: `${formType} wurde erfolgreich gesendet`,
      data
    };

  } catch (error) {
    console.error(`Fehler beim Senden des ${formType}:`, error);

    // Benutzerfreundliche Fehlermeldungen
    if (error.message.includes('Failed to fetch')) {
      throw new Error('Verbindung zum Server fehlgeschlagen. Bitte überprüfen Sie Ihre Internetverbindung.');
    }

    throw new Error(error.message || 'E-Mail konnte nicht gesendet werden');
  }
};

/**
 * Allgemeines Kontaktformular
 */
export const sendContactForm = async (formData) => {
  const emailData = {
    name: formData.name,
    email: formData.email,
    phone: formData.phone || formData.telefon || '',
    message: formData.message || formData.nachricht || '',
    subject: formData.subject || formData.betreff || 'Allgemeine Anfrage',
    service: formData.service || '',
    priority: formData.priority || formData.prioritat || 'Normal'
  };

  return sendEmail(emailData, 'Kontaktformular');
};

/**
 * Immobilien-Anfrage (Kauf/Miete)
 */
export const sendPropertyInquiry = async (formData) => {
  const emailData = {
    name: formData.name,
    email: formData.email,
    phone: formData.phone || '',
    subject: `Immobilien-Anfrage: ${formData.propertyType || 'Allgemein'}`,
    message: `
IMMOBILIEN-ANFRAGE:
Immobilienart: ${formData.propertyType || 'Nicht angegeben'}
Budget: ${formData.budget || 'Nicht angegeben'}
Gewünschte Region: ${formData.location || 'Nicht angegeben'}
Anzahl Zimmer: ${formData.rooms || 'Nicht angegeben'}
Art der Anfrage: ${formData.inquiryType || 'Kauf'}

Nachricht:
${formData.message || 'Keine zusätzlichen Informationen'}
    `,
    service: 'Immobilien-Anfrage',
    priority: 'Normal'
  };

  return sendEmail(emailData, 'Immobilien-Anfrage');
};

/**
 * Finanzierungs-Anfrage
 */
export const sendFinanceInquiry = async (formData) => {
  const emailData = {
    name: formData.name,
    email: formData.email,
    phone: formData.phone || '',
    subject: 'Finanzierungs-Anfrage',
    message: `
FINANZIERUNGS-ANFRAGE:
Finanzierungsbedarf: ${formData.amount || formData.purchasePrice || 'Nicht angegeben'}
Objektart: ${formData.propertyType || 'Nicht angegeben'}
Eigenkapital: ${formData.equity || 'Nicht angegeben'}
Monatliches Einkommen: ${formData.monthlyIncome || 'Nicht angegeben'}
Beschäftigung: ${formData.employment || 'Nicht angegeben'}
Bevorzugter Kontakt: ${formData.preferredContact || 'E-Mail'}

Nachricht:
${formData.message || 'Keine zusätzlichen Informationen'}
    `,
    service: 'Finanzierungs-Anfrage',
    priority: 'Hoch'
  };

  return sendEmail(emailData, 'Finanzierungs-Anfrage');
};

/**
 * Bewerbung (Karriere)
 */
export const sendCareerApplication = async (formData) => {
  const emailData = {
    name: `${formData.vorname} ${formData.nachname}`,
    email: formData.email,
    phone: formData.telefon || '',
    subject: `Bewerbung: ${formData.ausbildungsberuf || 'Allgemein'}`,
    message: `
PERSÖNLICHE DATEN:
Name: ${formData.vorname} ${formData.nachname}
Geburtsdatum: ${formData.geburtsdatum || 'Nicht angegeben'}
Adresse: ${formData.adresse || 'Nicht angegeben'}

BILDUNG & QUALIFIKATION:
Schulabschluss: ${formData.schulabschluss || 'Nicht angegeben'}
Gewünschter Ausbildungsberuf: ${formData.ausbildungsberuf || 'Nicht angegeben'}
Gewünschter Starttermin: ${formData.startdatum || 'Nicht angegeben'}

MOTIVATION:
${formData.motivation || 'Keine Angabe'}

ERFAHRUNGEN:
${formData.erfahrungen || 'Keine Angabe'}
    `,
    service: 'Bewerbung',
    priority: 'Normal'
  };

  return sendEmail(emailData, 'Bewerbung');
};

/**
 * Immobilien-Bewertung (Verkauf)
 */
export const sendPropertyValuation = async (formData) => {
  const emailData = {
    name: formData.name,
    email: formData.email,
    phone: formData.phone || '',
    subject: 'Immobilien-Bewertungsanfrage',
    message: `
OBJEKTDATEN:
Immobilienart: ${formData.propertyType || 'Nicht angegeben'}
Wohnfläche: ${formData.livingSpace || 'Nicht angegeben'} m²
Anzahl Zimmer: ${formData.rooms || 'Nicht angegeben'}
Baujahr: ${formData.yearBuilt || 'Nicht angegeben'}
Zustand: ${formData.condition || 'Nicht angegeben'}
Lage: ${formData.location || 'Nicht angegeben'}

BESONDERE MERKMALE:
${formData.specialFeatures || 'Keine'}

KONTAKTPRÄFERENZ:
${formData.preferredContact || 'E-Mail'} - ${formData.preferredTime || 'Keine Präferenz'}

ZUSÄTZLICHE INFORMATIONEN:
${formData.message || 'Keine'}
    `,
    service: 'Immobilien-Bewertung',
    priority: 'Hoch'
  };

  return sendEmail(emailData, 'Immobilien-Bewertung');
};

/**
 * Termin-Anfrage
 */
export const sendAppointmentRequest = async (formData) => {
  const emailData = {
    name: formData.name,
    email: formData.email,
    phone: formData.telefon || formData.phone || '',
    subject: `Termin-Anfrage: ${formData.serviceType || 'Beratung'}`,
    message: `
TERMIN-ANFRAGE:
Wunschtermin: ${formData.wunschtermin || formData.appointmentDate || 'Nicht angegeben'}
Art der Dienstleistung: ${formData.serviceType || 'Beratung'}

Nachricht:
${formData.nachricht || formData.message || 'Keine zusätzlichen Informationen'}
    `,
    service: 'Termin-Anfrage',
    priority: 'Normal'
  };

  return sendEmail(emailData, 'Termin-Anfrage');
};

/**
 * Sidebar Kontaktformular (vereinfacht)
 */
export const sendSidebarContact = async (formData) => {
  const emailData = {
    name: formData.name,
    email: formData.email,
    phone: formData.phone || '',
    subject: formData.subject || 'Sidebar Anfrage',
    message: `
Immobilienart: ${formData.propertyType || 'Nicht spezifiziert'}
Budget: ${formData.budget || 'Nicht angegeben'}

Nachricht:
${formData.message || 'Keine Nachricht'}
    `,
    service: 'Sidebar Kontakt',
    priority: 'Normal'
  };

  return sendEmail(emailData, 'Sidebar Kontakt');
};

/**
 * Hilfsfunktion: E-Mail Konfiguration prüfen
 */
export const checkEmailConfiguration = () => {
  const config = {
    apiUrl: emailConfig.apiUrl,
    companyEmail: emailConfig.companyEmail,
    companyName: emailConfig.companyName,
    backendAvailable: true
  };

  console.log('E-Mail Konfiguration:', config);
  return config;
};

/**
 * Standard Erfolgs- und Fehlermeldungen
 */
export const emailMessages = {
  success: {
    contact: 'Vielen Dank für Ihre Anfrage! Wir melden uns schnellstmöglich bei Ihnen.',
    propertyInquiry: 'Ihre Immobilien-Anfrage wurde erfolgreich gesendet. Unser Team wird sich zeitnah bei Ihnen melden.',
    financeInquiry: 'Vielen Dank für Ihre Finanzierungsanfrage! Unser Finanzierungsexperte wird sich in Kürze bei Ihnen melden.',
    careerApplication: 'Vielen Dank für Ihre Bewerbung! Wir prüfen Ihre Unterlagen und melden uns bald bei Ihnen.',
    propertyValuation: 'Ihre Bewertungsanfrage wurde erfolgreich übermittelt. Unser Team wird sich innerhalb von 24 Stunden bei Ihnen melden.',
    appointment: 'Terminanfrage gesendet! Wir kontaktieren Sie zur Bestätigung.',
    sidebar: 'Vielen Dank für Ihre Nachricht! Wir melden uns schnellstmöglich bei Ihnen.'
  },
  error: {
    network: 'Verbindungsfehler. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
    configuration: 'E-Mail Service ist momentan nicht verfügbar. Bitte kontaktieren Sie uns direkt.',
    general: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt.'
  }
};
